import {InfiniteScroll, WithStyledProps} from '@coolcolduk/ui-mui';
import {uniqueObjectWithKey} from '@coolcolduk/util';
import {Box, CircularProgress, Stack, styled} from '@mui/material';
import {useCallback, useEffect, useState} from 'react';
import {QueueJob, QueueJobContent} from '../../__generated__/resolvers-types';
import {useListQueueJobQuery} from '../../hook/useListQueueJobQuery';
import CenteredText from '../centeredText/CenteredText';
import {JobListItem} from './JobListItem';

export interface JobListProps {
  content: QueueJobContent;
}

const PAGE_SIZE = 20;

function JobListInternal({className, content}: WithStyledProps<JobListProps>) {
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [jobs, setJobs] = useState<QueueJob[]>([]);

  const {data, loading, error} = useListQueueJobQuery({
    content,
    limit: PAGE_SIZE,
    offset,
  });

  useEffect(() => {
    if (data?.listQueueJob) {
      setJobs((prev) => {
        const newJobs = uniqueObjectWithKey([...prev, ...data.listQueueJob], 'id');
        return newJobs.sort((a, b) => b.creationTimestamp - a.creationTimestamp);
      });
      setHasMore(data.listQueueJob.length === PAGE_SIZE);
    }
  }, [data]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      setOffset((prev) => prev + PAGE_SIZE);
    }
  }, [loading, hasMore]);

  if (error) {
    return <CenteredText text={`Error loading jobs: ${error.message}`} minHeight={300} />;
  }

  if (!loading && data?.listQueueJob.length === 0) {
    return <CenteredText text="No job available" minHeight={300} />;
  }
  return (
    <InfiniteScroll
      className={className}
      dataLength={jobs.length}
      next={loadMore}
      hasMore={hasMore}
      loader={
        <Box p={2} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      }
      scrollThreshold={0.8}
      uniqueKey={`job-list-${content}`}
      height={600}
    >
      <Stack spacing={2}>
        {jobs.map((job) => (
          <JobListItem key={job.id} job={job} />
        ))}
      </Stack>
    </InfiniteScroll>
  );
}

export default styled(JobListInternal, {name: 'CcJobList'})<JobListProps>();
